/**
 * The route panel: the waypoint list (reorder, rename, per-waypoint altitude,
 * delete), a search box over `/api/geo/search`, and the airspace check.
 *
 * The airspace check is a **hint, not a clearance** — it is a lookup against the
 * openAIP mirror with published simplifications, which the API returns as stable
 * `assumptions` codes and which this panel always prints.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
  type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent,
} from 'react';
import * as api from '../api';
import { Freshness } from '../components/Freshness';
import { InfoPopover } from '../components/InfoPopover';
import { useT } from '../i18n';
import { usePlanRev, usePlanStore, useRoute } from '../planContext';
import {
  DEFAULT_CHECK_ALT_FT,
  MAX_ROUTE_LEGS,
  MAX_WAYPOINTS,
  MAX_WAYPOINT_NAME,
  checkAltitudes,
  isSameSpot,
  nextWaypointName,
  waypointFromHit,
  type Waypoint,
} from '../route';
import { CORRIDOR_OPTIONS, type Spark, type TerrainRow } from '../terrain';
import { useTerrain } from '../terrainContext';
import { windLegText } from '../winds';
import { useWinds } from '../windsContext';
import {
  SHEET_MIN_PX,
  cycleSheet as nextSheet,
  readSheet,
  snapSheet,
  writeSheet,
  type SheetState,
} from './sheet';

const LS_PANEL = 'avia.map.panel';

const readOpen = (): boolean => {
  try {
    return localStorage.getItem(LS_PANEL) !== '0';
  } catch {
    return true;
  }
};

/**
 * The winds aloft, leg by leg: "W/V 314/3 · +17 °C · ICON-D2 12Z".
 *
 * It sits beside the airspace check rather than inside it, because the two
 * answer different questions about the same legs and one of them can fail while
 * the other is on the screen — folding the wind into the check's own leg blocks
 * would make it disappear whenever the check did.
 *
 * Three states per leg, and they are not the same thing: no wind at all, a wind
 * this fill wrote, and a wind that was written for a leg whose midpoint has since
 * moved (**veraltet**). The last one keeps its value on purpose — the wind at the
 * old midpoint is still the best number on the form — but it must not go on
 * looking like an answer to the leg that is drawn now.
 */
/**
 * A waypoint row's `⋯` menu — Auf Karte zeigen · Nach oben · Nach unten · Löschen.
 *
 * §5.2.1 asks for exactly this and forbids what was here before: a row of four
 * unlabelled glyphs. Four labelled commands behind one control also gives every
 * one of them the 44 px §14.4 wants without spending 180 px of a 420 px
 * inspector on icons — and on WebKit at 390 px the glyph row overflowed the
 * viewport entirely, which made reorder and delete reachable by nothing at all.
 */
function RowMenu({
  label,
  items,
}: {
  label: string;
  items: readonly { key: string; label: string; danger?: boolean; disabled?: boolean; onSelect(): void }[];
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: globalThis.MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="wpmenu" ref={box}>
      <button
        type="button"
        className="wpmenu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen(v => !v)}
      >
        ⋯
      </button>
      {open ? (
        <div className="ae-menu wpmenu-pop" role="menu">
          {items.map(it => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              {...(it.danger ? { className: 'danger' } : {})}
              {...(it.disabled ? { disabled: true } : {})}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function WindsList({ waypoints }: { waypoints: readonly Waypoint[] }) {
  const t = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const winds = useWinds();
  const legs = Math.min(Math.max(0, waypoints.length - 1), MAX_ROUTE_LEGS);

  const rows = useMemo(() => {
    const out: { leg: number; from: string; to: string; text: string; stale: boolean }[] = [];
    for (let j = 1; j <= legs; j++) {
      const id = 'leg' + j + '_wind';
      // only a value THIS source owns is described as a forecast; a wind the pilot
      // typed is the pilot's and is not ours to caption with a model name
      const own = store.derivedSource(id) === 'winds' ? store.val(id) : '';
      out.push({
        leg: j,
        from: waypoints[j - 1]?.name ?? '',
        to: waypoints[j]?.name ?? '',
        text: windLegText(winds?.legs.get(j), own),
        stale: winds?.stale.has(j) === true && own !== '',
      });
    }
    return out;
    // `rev` is the store's own change signal — the W/V cells are half of this
  }, [legs, waypoints, store, winds, rev]);

  if (!winds || legs === 0) return null;

  return (
    <section className="mapcheck mapwinds">
      <h4>
        {t('winds.title')}
        {/* status only: the one ⟳ for the forecast is in the Formular's route
            section, and a second one over the map would be the duplicate this
            pass exists to remove */}
        <Freshness family="winds" action={false} label={false} />
      </h4>
      <ul>
        {rows.map(r => (
          <li key={r.leg}>
            <b>{r.leg}</b> {r.from} → {r.to}
            <span className="note"> {r.text || t('winds.none')}</span>
            {r.stale ? <span className="mapstale"> · {t('winds.stale')}</span> : null}
          </li>
        ))}
      </ul>
      {/* The licence is a condition of use, not decoration, and the forecast is not
          a tile source — MapLibre's attribution control (which carries openAIP and
          Mapterhorn) never mentions it. On a phone the map REPLACES the form, so
          without this line the winds would be displayed on a screen that credits
          and links nothing. Same catalogue string as the Formular's own credit,
          so the two cannot drift apart. */}
      <p className="note wxcredit">
        <a className="cite" href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">
          {t('winds.credit')}
        </a>
      </p>
    </section>
  );
}

/**
 * One leg's terrain profile as an inline SVG — no chart library, and no
 * arithmetic in this component: `sparkline()` in `terrain.ts` produced every
 * coordinate, so the rule ("null breaks the line, the MSA is the top of the box")
 * is pinned down by tests rather than by looking at it.
 *
 * `preserveAspectRatio="none"` lets 100 abstract units stretch to whatever width
 * the panel has — 390 px on a phone, 300 in the desktop rail — and
 * `vector-effect: non-scaling-stroke` is what keeps the line one pixel wide when
 * it does.
 */
function Sparkline({ spark, label }: { spark: Spark; label: string }) {
  return (
    <svg
      className="tspark"
      viewBox={`0 0 ${spark.w} ${spark.h}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      {spark.areas.map((d, i) => (
        <path key={'a' + i} d={d} className="tspark-fill" />
      ))}
      {spark.lines.map((pts, i) => (
        <polyline key={'l' + i} points={pts} className="tspark-line" vectorEffect="non-scaling-stroke" />
      ))}
      {spark.ticks.map((tk, i) => (
        <line
          key={'t' + i}
          x1={tk.x}
          y1={tk.y}
          x2={tk.x}
          y2={spark.h}
          className={'tspark-obst' + (tk.exact ? '' : ' approx')}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {spark.msaY === null ? null : (
        <line
          x1={0}
          y1={spark.msaY}
          x2={spark.w}
          y2={spark.msaY}
          className="tspark-msa"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}

/** The obstacle kinds openAIP publishes that have a word of their own in the catalogue. */
const OBSTACLE_KINDS: readonly string[] = ['chimney', 'building', 'wind_turbine', 'tower'];

/**
 * „Hindernis Mast ≥ 1119 ft" — the ≥ is `exact: false`, and it is not decoration.
 *
 * Most obstacles are published with no name at all. What they DO carry is a kind,
 * which is translated here rather than substituted raw: the slug went straight
 * into the sentence before this and printed „Hindernis Obstacle 716 ft" into a
 * German UI. An unknown kind gets the height on its own — a nameless obstacle is
 * still a fact, and „Hindernis 716 ft" says it.
 */
function obstacleText(t: ReturnType<typeof useT>, row: TerrainRow): string {
  const o = row.obstacle;
  if (!o || o.ft == null) return '';
  const name = o.name || (OBSTACLE_KINDS.includes(o.kind) ? t('obst.kind.' + o.kind) : '');
  const key = name ? 'terrain.obst' : 'terrain.obst.plain';
  return t(o.exact ? key : key + '.min', { name, ft: o.ft });
}

/**
 * The terrain, leg by leg: the highest ground and where it is, the tallest
 * obstacle, the suggested SafeAlt with the rule that produced it — and the ✓ that
 * writes that one leg's cell.
 *
 * It sits beside the airspace check and the winds rather than inside either: all
 * three describe the same legs and answer different questions, and one of them
 * failing must not take the other two off the screen.
 *
 * With no provider it renders nothing: the shared read-only view fetches no
 * profile, and what that page has to show — the stored per-leg summary and the
 * attribution — is in the Formular's route section, which it always renders.
 */
function TerrainList({ waypoints }: { waypoints: readonly Waypoint[] }) {
  const t = useT();
  const terrain = useTerrain();
  const rows = terrain?.rows ?? [];
  if (!terrain || waypoints.length < 2) return null;
  // What the profile cannot answer — a 50 m grid, obstacles published without a
  // height. True, and not a sentence a pilot needs under every leg: it goes into
  // the section's ⓘ, under the two sentences that say what the numbers are.
  const assumptions = terrain.profile?.assumptions ?? [];
  const limits = assumptions.length
    ? t('map.check.assumptions') + ' ' + assumptions.map(a => t('map.assume.' + a)).join(' · ')
    : '';

  return (
    <section className="mapcheck mapterrain">
      <h4>
        {t('terrain.title')}
        <Freshness family="terrain" action={false} label={false} {...(limits ? { info: limits } : {})} />
      </h4>
      <div className="tctl">
        <label>
          <span>{t('terrain.corridor')}</span>
          <select
            className="mchip msel"
            value={String(terrain.corridor)}
            aria-label={t('terrain.corridor.aria')}
            onChange={e => terrain.setCorridor(Number(e.target.value))}
          >
            {CORRIDOR_OPTIONS.map(nm => (
              <option key={nm} value={nm}>
                {t('terrain.corridor.nm', { nm })}
              </option>
            ))}
          </select>
        </label>
      </div>
      {rows.length === 0 && !terrain.busy && !terrain.error ? (
        <p className="note">{t('terrain.none')}</p>
      ) : null}
      {rows.map(r => (
        <div className="mapleg tleg" key={r.leg}>
          <div className="mapleg-head">
            {t('terrain.leg', { n: r.leg, from: r.from, to: r.to })}
          </div>
          {r.spark ? (
            <Sparkline spark={r.spark} label={t('terrain.spark.aria', { n: r.leg })} />
          ) : null}
          <div className="tnums">
            <span className="note">
              {r.maxTerrainFt == null
                ? t('terrain.nodata')
                : r.maxAtNm == null
                  ? t('terrain.max.plain', { ft: r.maxTerrainFt })
                  : t('terrain.max', { ft: r.maxTerrainFt, nm: r.maxAtNm.toFixed(1) })}
            </span>
            {r.obstacle && r.obstacle.ft != null ? (
              <span className="note">{obstacleText(t, r)}</span>
            ) : null}
            {r.msaFt == null ? null : (
              <span className="tmsa">
                {t('terrain.msa', { ft: r.msaFt })}
                <span className="note">
                  {' '}
                  {t(r.msaRule === 'mountain' ? 'terrain.rule.mountain' : 'terrain.rule.flat')}
                </span>
              </span>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}

export interface RoutePanelProps {
  readOnly: boolean;
  /** narrow screens: render as a bottom sheet over the map instead of below it */
  sheet: boolean;
  /**
   * The inspector's Strecke tab: the list is a pane beside the map, not an
   * overlay on it, so it renders its contents with no aside, no collapse chrome
   * and no positioning of its own. The pane draws its own header.
   */
  embedded?: boolean;
  /** the drawn route's length, for the collapsed sheet's one-line summary */
  totalNm: number;
  onFit(): void;
  onFlyTo(lat: number, lon: number): void;
  mapCentre(): { lat: number; lon: number } | null;
  onHighlight(ids: string[]): void;
}

export function RoutePanel({ readOnly, sheet, embedded = false, totalNm, onFit, onFlyTo, mapCentre, onHighlight }: RoutePanelProps) {
  const t = useT();
  const store = usePlanStore();
  const route = useRoute();
  const rev = usePlanRev();
  const waypoints = useMemo(() => route?.waypoints ?? [], [route]);

  const [open, setOpen] = useState(readOpen);
  const [sheetState, setSheetState] = useState<SheetState>(readSheet);
  /** live height while the grab bar is dragged; null when it is not */
  const [dragH, setDragH] = useState<number | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<api.GeoHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<unknown>(null);

  const [check, setCheck] = useState<api.RouteCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkErr, setCheckErr] = useState<unknown>(null);

  const commit = useCallback(
    (next: Waypoint[]) => {
      if (readOnly) return;
      store.setWaypoints(next.slice(0, MAX_WAYPOINTS));
    },
    [store, readOnly],
  );

  useEffect(() => {
    try {
      localStorage.setItem(LS_PANEL, open ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [open]);

  useEffect(() => writeSheet(sheetState), [sheetState]);

  /* ---------------- the sheet's grab bar ---------------- */

  const cycleSheet = useCallback(() => setSheetState(nextSheet), []);

  /**
   * Dragging happens on this element, never on the map: the sheet is a sibling
   * of the canvas, so MapLibre never sees the pointer, and `touch-action: none`
   * on the bar keeps the browser from scrolling the page instead.
   */
  const onGrab = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const el = asideRef.current;
    const box = el?.parentElement;
    if (!el || !box) return;
    const maxH = box.getBoundingClientRect().height;
    const startY = e.clientY;
    const startH = el.getBoundingClientRect().height;
    const at = (y: number) => Math.max(SHEET_MIN_PX, Math.min(maxH, startH + (startY - y)));
    let moved = false;
    e.currentTarget.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      if (Math.abs(startY - ev.clientY) > 6) moved = true;
      setDragH(at(ev.clientY));
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragH(null);
      // a tap is a drag that went nowhere: step to the next height
      if (!moved) {
        cycleSheet();
        return;
      }
      setSheetState(snapSheet(sheetState, at(ev.clientY), maxH, startY - ev.clientY));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  /** Enter/Space on the grab bar: a click with no pointer behind it. */
  const onGrabClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (e.detail === 0) cycleSheet();
  };

  /* ---------------- search ---------------- */

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits([]);
      setSearching(false);
      setSearchErr(null);
      return;
    }
    const ctl = new AbortController();
    const near = mapCentre();
    const timer = setTimeout(() => {
      setSearching(true);
      setSearchErr(null);
      void api
        .searchGeo(term, { limit: 12, ...(near ? { near } : {}), signal: ctl.signal })
        .then(rows => {
          setHits(rows);
          setSearching(false);
        })
        .catch((e: unknown) => {
          if (api.isAbort(e)) return;
          setSearchErr(e);
          setHits([]);
          setSearching(false);
        });
    }, 280);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [q, mapCentre]);

  /* ---------------- airspace check ---------------- */

  /**
   * The altitude band each waypoint is checked at. The map's own `alt_ft` wins;
   * failing that the Höhe/FL cell of the leg that ends there; failing that the
   * server's `default_alt_ft`.
   */
  const checkPoints = useMemo<api.RouteCheckPoint[]>(() => {
    const alts = checkAltitudes(waypoints, leg => store.val('leg' + leg + '_alt'));
    return waypoints.map((w, i) => {
      const a = alts[i];
      return { lat: w.lat, lon: w.lon, ...(a != null ? { alt_ft: a } : {}) };
    });
    // `rev` is in the deps because half of this comes out of the FORM: typing into the
    // Höhe/FL column changes the altitude the check runs at, and `store` is a stable
    // singleton whose identity never moves. checkKey below de-duplicates, so a keystroke
    // that does not change an altitude still costs no request.
  }, [waypoints, store, rev]);

  const checkKey = useMemo(() => JSON.stringify(checkPoints), [checkPoints]);

  useEffect(() => {
    if (checkPoints.length < 2) {
      setCheck(null);
      setCheckErr(null);
      setChecking(false);
      onHighlight([]);
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      setChecking(true);
      setCheckErr(null);
      void api
        .checkRoute(checkPoints, { defaultAltFt: DEFAULT_CHECK_ALT_FT, signal: ctl.signal })
        .then(res => {
          setCheck(res);
          setChecking(false);
          onHighlight([...new Set(res.legs.flatMap(l => l.airspaces.map(a => a.id)))]);
        })
        .catch((e: unknown) => {
          if (api.isAbort(e)) return;
          setCheckErr(e);
          setCheck(null);
          setChecking(false);
          onHighlight([]);
        });
    }, 500);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
    // checkKey is the value identity of checkPoints — re-running on the array
    // identity alone would fire the check on every unrelated store notification
  }, [checkKey]);

  /* ---------------- waypoint edits ---------------- */

  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= waypoints.length) return;
    const next = [...waypoints];
    const a = next[i];
    const b = next[j];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    commit(next);
  };

  const remove = (i: number) => commit(waypoints.filter((_, k) => k !== i));

  const patch = (i: number, p: Partial<Waypoint>) =>
    commit(waypoints.map((w, k) => (k === i ? { ...w, ...p } : w)));

  const addHit = (hit: api.GeoHit) => {
    if (waypoints.length >= MAX_WAYPOINTS) return;
    const w = waypointFromHit(hit, waypoints);
    // adding the same airport twice in a row is a 0 NM leg on a course of 360° in the
    // printed navlog — the map's own click path already refuses to stack waypoints
    setQ('');
    setHits([]);
    if (isSameSpot(waypoints[waypoints.length - 1], w)) return;
    commit([...waypoints, w]);
  };

  const dropped = Math.max(0, waypoints.length - 1 - MAX_ROUTE_LEGS);

  const body = (
    <div className="mappanel-body">
      {!readOnly ? (
        <div className="mapsearch">
          <input
            type="text"
            value={q}
            placeholder={t('map.search.ph')}
            aria-label={t('map.search.aria')}
            autoComplete="off"
            spellCheck={false}
            onChange={e => setQ(e.target.value)}
          />
          {searching ? <span className="note">{t('map.search.busy')}</span> : null}
          {searchErr ? <span className="err">{api.apiMessage(t, searchErr)}</span> : null}
          {hits.length ? (
            <ul className="maphits">
              {hits.map(h => (
                <li key={h.layer + h.id}>
                  <button type="button" onClick={() => addHit(h)}>
                    <b>{h.ident ?? h.name}</b>
                    <span>
                      {h.ident ? h.name + ' · ' : ''}
                      {t('map.layer.' + h.layer)}
                      {h.country ? ' · ' + h.country.toUpperCase() : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {q.trim().length >= 2 && !searching && !searchErr && hits.length === 0 ? (
            <span className="note">{t('map.search.none')}</span>
          ) : null}
        </div>
      ) : null}

      {waypoints.length === 0 ? (
        <p className="note">{readOnly ? t('map.wp.emptyRO') : t('map.wp.empty')}</p>
      ) : (
        <ol className="mapwps">
          {waypoints.map((w, i) => (
            <li key={w.id}>
              <span className="idx" title={t('map.wp.kind.' + w.kind)}>
                {i === 0 ? '▲' : i === waypoints.length - 1 ? '▼' : i}
              </span>
              <input
                type="text"
                className="wpname"
                value={w.name}
                maxLength={MAX_WAYPOINT_NAME}
                readOnly={readOnly}
                aria-label={t('map.wp.name')}
                onChange={e => patch(i, { name: e.target.value })}
                // a checkpoint with no name prints an empty Kontrollpunkt cell that
                // still claims to come from the map; parseRoute already substitutes a
                // name when READING a document, so do the same when writing one
                onBlur={e => {
                  if (e.target.value.trim() !== '') return;
                  patch(i, {
                    name:
                      w.icao ??
                      nextWaypointName(waypoints.filter((_, k) => k !== i)),
                  });
                }}
              />
              <input
                type="text"
                className="wpalt"
                inputMode="numeric"
                value={w.alt_ft == null ? '' : String(w.alt_ft)}
                placeholder={t('map.wp.alt')}
                readOnly={readOnly}
                aria-label={t('map.wp.alt.aria', { n: i + 1 })}
                onChange={e => {
                  const v = e.target.value.replace(/[^\d]/g, '');
                  patch(i, { alt_ft: v === '' ? null : Math.min(60000, Number(v)) });
                }}
              />
              <RowMenu
                label={t('map.wp.menu', { n: i + 1 })}
                items={[
                  { key: 'centre', label: t('map.wp.centre'), onSelect: () => onFlyTo(w.lat, w.lon) },
                  ...(readOnly
                    ? []
                    : [
                        { key: 'up', label: t('map.wp.up'), disabled: i === 0, onSelect: () => move(i, -1) },
                        {
                          key: 'down',
                          label: t('map.wp.down'),
                          disabled: i === waypoints.length - 1,
                          onSelect: () => move(i, 1),
                        },
                        { key: 'remove', label: t('map.wp.remove'), danger: true, onSelect: () => remove(i) },
                      ]),
                ]}
              />
            </li>
          ))}
        </ol>
      )}

      {dropped > 0 ? <p className="mapwarn">{t('map.cap.legs', { n: dropped })}</p> : null}
      {waypoints.length >= MAX_WAYPOINTS ? <p className="mapwarn">{t('map.cap.waypoints')}</p> : null}

      {/* The inspector has a Wetter tab and a Gelände tab, and they own this
          material — in the pane style, with their own charts and their own
          corridor control. Rendering it here too put the whole Gelände pane one
          tab away from the Gelände pane, drawn differently (§5.2.1 lists search,
          the waypoint list and the freshness chips, and nothing else). The map's
          own sheet still carries all three: there the panel IS the map. */}
      {embedded ? null : <WindsList waypoints={waypoints} />}

      {embedded ? null : <TerrainList waypoints={waypoints} />}

      {embedded ? null : (
      <section className="mapcheck">
        <h4>
          {t('map.check.title')}
          {/* the same ⓘ as every data-bearing section: who the airspace comes from,
              and what this lookup does not cover */}
          <InfoPopover
            body={t('src.info.airspace')}
            links={[{ href: 'https://www.openaip.net/', text: 'openaip.net' }]}
            {...(check?.assumptions.length
              ? {
                  extra:
                    t('map.check.assumptions') +
                    ' ' +
                    check.assumptions.map(a => t('map.assume.' + a)).join(' · '),
                }
              : {})}
          />
        </h4>
        {/* the one caveat that stays on screen: it is a safety statement, not provenance */}
        <p className="mapwarn">{t('map.check.hint')}</p>
        {checking ? <p className="note">{t('map.check.busy')}</p> : null}
        {checkErr ? <p className="err">{api.apiMessage(t, checkErr)}</p> : null}
        {check ? (
          <>
            <p className="note">{t('map.check.total', { nm: check.total_nm.toFixed(1) })}</p>
            {check.legs.map(leg => (
              <div className="mapleg" key={leg.leg}>
                <div className="mapleg-head">
                  {t('map.check.leg', {
                    n: leg.leg + 1,
                    from: waypoints[leg.leg]?.name ?? '',
                    to: waypoints[leg.leg + 1]?.name ?? '',
                    nm: leg.dist_nm.toFixed(1),
                  })}
                </div>
                {leg.airspaces.length === 0 ? (
                  <div className="note">{t('map.check.clear')}</div>
                ) : (
                  <ul>
                    {leg.airspaces.map(a => (
                      <li key={a.id}>
                        <b>{a.kind}</b>
                        {a.icao_class ? ' ' + a.icao_class : ''} {a.name}
                        <span className="note">
                          {' '}
                          {(a.lower_raw ?? '?') + ' – ' + (a.upper_raw ?? '?')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </>
        ) : null}
      </section>
      )}

      {/* openAIP's licence asks for a credit wherever its data is shown, and this
          panel is full of it: the airspaces on the route, the obstacles in the
          terrain block, the kind of every waypoint. MapLibre's own control carries
          the same line — but on a phone the raised sheet covers the map entirely,
          which is exactly when this panel is doing the showing. Same catalogue
          string as the Formular's credit, so the two cannot drift apart. */}
      <p className="note wxcredit geocredit">
        <a className="cite" href="https://www.openaip.net/" target="_blank" rel="noopener noreferrer">
          {t('geo.credit')}
        </a>
      </p>
    </div>
  );

  /* ---------------- the inspector pane (wide) ---------------- */

  if (embedded) return <div className="ae-routepane">{body}</div>;

  /* ---------------- the sheet (narrow) ---------------- */

  if (sheet) {
    const summary =
      waypoints.length === 0
        ? t('map.sheet.empty')
        : t('map.sheet.summary', { n: waypoints.length, nm: totalNm.toFixed(1) });
    return (
      <aside
        ref={asideRef}
        className={'mappanel sheet ' + sheetState + (dragH !== null ? ' dragging' : '')}
        style={dragH !== null ? { height: dragH + 'px' } : undefined}
      >
        <button
          type="button"
          className="mapsheet-grab"
          title={t('map.sheet.grab')}
          aria-expanded={sheetState !== 'collapsed'}
          onPointerDown={onGrab}
          onClick={onGrabClick}
        >
          <span className="mapsheet-grip" aria-hidden="true" />
          <span className="mapsheet-sum">{summary}</span>
          <span className="mapsheet-chev" aria-hidden="true">
            {sheetState === 'collapsed' ? '\u2303' : '\u2304'}
          </span>
        </button>
        {sheetState === 'collapsed' ? null : body}
      </aside>
    );
  }

  /* ---------------- the panel (wide) ---------------- */

  return (
    <aside className={'mappanel' + (open ? '' : ' closed')}>
      <div className="mappanel-head">
        <button type="button" className="mchip" aria-expanded={open} onClick={() => setOpen(v => !v)}>
          {t('map.wp.title')} ({waypoints.length})
        </button>
        <button type="button" className="mchip" onClick={onFit} disabled={waypoints.length === 0}>
          {t('map.fit')}
        </button>
      </div>
      {open ? body : null}
    </aside>
  );
}
